/* AddUserDialog — system admin creates a user; backend issues their
   Scan2Call QR and emails the printable poster + sign-in instructions. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserPlus, QrCode } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [estate, setEstate] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/users/create`,
        { email: email.trim().toLowerCase(), name: name.trim() || null, estate: estate.trim() || null },
        { withCredentials: true },
      );
      toast.success(r.data.email_sent
        ? `User created — QR code emailed to ${r.data.email}`
        : `User created, but the email failed to send — share their QR manually`);
      setEmail("");
      setName("");
      setEstate("");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="add-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Add user
          </DialogTitle>
          <DialogDescription className="font-body text-xs flex items-start gap-1.5">
            <QrCode className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            We'll create the account, issue their Scan2Call QR and email them the
            printable poster with sign-in instructions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Email *</Label>
            <Input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="resident@example.com"
              className="rounded-sm h-10 text-sm"
              data-testid="add-user-email-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Name (optional)</Label>
            <Input
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              placeholder="Shown on their QR poster"
              className="rounded-sm h-10 text-sm"
              data-testid="add-user-name-input"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1 block">Estate (optional)</Label>
            <Input
              value={estate}
              maxLength={60}
              onChange={(e) => setEstate(e.target.value)}
              placeholder="e.g. Sunrise Gardens Estate"
              className="rounded-sm h-10 text-sm"
              data-testid="add-user-estate-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-body text-xs" data-testid="add-user-cancel">
            Cancel
          </Button>
          <Button
            onClick={create}
            disabled={busy || !email.trim()}
            className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs"
            data-testid="add-user-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Create & email QR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
